<template>
  <div>
    <div v-if="!isEditing" :class="$style.container">
      <label :class="[$style.container__selector, $style.selector]">
        <span>テンプレート:</span>
        <DolphinSelector
          :class="$style.selector__action"
          placeholder="---------------"
          :options="templateSelectorOptions"
          v-model="selectedId"
          @change="selectTemplate"
        />
      </label>
      <div :class="[$style.container__actions, $style.buttons]">
        <template v-if="isSelected">
          <FlatButton
            :class="$style.buttons__item"
            @click="load"
            variant="primary"
            size="medium"
          >
            テンプレートを挿入
          </FlatButton>
          <FlatButton
            :class="$style.buttons__item"
            @click="editTemplate"
            variant="secondary"
            size="medium"
          >
            テンプレートを編集
          </FlatButton>
        </template>
        <FlatButton
          :class="$style.buttons__item"
          :disabled="body.length === 0"
          @click="handleCreateTemplate"
          variant="secondary"
          size="medium"
        >
          テンプレートとして新規保存
        </FlatButton>
      </div>
    </div>

    <div :class="[$style.container, $style['container--editing']]" v-else>
      <p>
        テンプレート名:
        <DolphinTextInput
          :class="$style.container__label"
          v-model="editingItemState.label"
          placeholder="テンプレート名"
        />
        を編集中
      </p>
      <DolphinTextarea
        autoGrow
        :class="$style.container__body"
        v-model="editingItemState.body"
        placeholder="テンプレートの本文を記入してください"
      />
      <MessageAttachmentActions
        v-model="editingItemState.selections"
        :isDeleteAllSelectionsAllowed="false"
      />
      <div :class="[$style.container__actions, $style.buttons]">
        <FlatButton
          variant="caution"
          size="medium"
          :class="$style.buttons__item"
          @click="requestRemoveTemplate"
        >
          このテンプレートを削除
        </FlatButton>
        <component :is="removeConfirmationModal.ControlledModal">
          <ConfirmationModal
            title="テンプレートの削除"
            buttonText="削除する"
            buttonVariant="caution"
            @confirm="removeTemplate"
            @close="closeRemoveConfirmationTemplate"
          >
            このテンプレートを削除しますか？
          </ConfirmationModal>
        </component>
        <FlatButton
          variant="secondary"
          :class="$style.buttons__item"
          @click="cancelEditingTemplate"
        >
          キャンセル
        </FlatButton>
        <FlatButton
          :class="$style.buttons__item"
          @click="handleApplyEditedTemplate"
          :disabled="editingItemState.body.length === 0"
          variant="primary"
        >
          このテンプレートの変更を保存
        </FlatButton>
      </div>
    </div>
    <component :is="includesUserNameModal.ControlledModal">
      <KartePlannerCommentIncludesUserNameConfirmation
        :maybe-user-names="includesUserNames"
        @submit="handleSubmitIncludesUserNameModal"
        @cancel="includesUserNameModal.closeModal()"
      />
    </component>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinTextarea from '@/components/atoms/DolphinTextarea.vue'
import ConfirmationModal from '@/components/molecules/ConfirmationModal.vue'
import MessageAttachmentActions from '@/components/organisms/MessageAttachmentActions/MessageAttachmentActions.vue'

import { useAlert } from '@/libs/alertPortal'
import { useModal } from '@/hooks/useModal'
import { useKarteCommentTemplate } from '../hooks/useKarteCommentTemplate'

import type { Option } from '@/types/app'
import FlatButton from '@/components/atoms/FlatButton.vue'
import KartePlannerCommentIncludesUserNameConfirmation from '@/components/organisms/KartePlannerCommentIncludesUserNameConfirmation/KartePlannerCommentIncludesUserNameConfirmation.vue'
import { usePreviewKartePlannerCommentTemplateMutation } from './KartePlannerCommentTemplateSelector.generated'
import { useLogger } from '@/logger/logger'

type TemplateSelectorOption = Option<string | null>

const DEFAULT_LABEL_LENGTH = 10

const props = withDefaults(
  defineProps<{ body?: string; selections?: string[] }>(),
  { body: '', selections: () => [] }
)

const emit = defineEmits<{
  (event: 'load', body: string, selections: string[]): boolean
  (event: 'edit'): void
  (event: 'finishEdit'): void
}>()

const {
  templates,
  isSelected,
  isEditing,
  selectedId,
  selectedItem,
  edit,
  cancelEditing,
  applyEdited,
  appendNewItem,
  removeEditingItem,
  editingItemState,
} = useKarteCommentTemplate()

const templateSelectorOptions = computed<TemplateSelectorOption[]>(() => [
  {
    value: null,
    text: '-----',
  },
  ...templates.value.map(item => ({
    value: item.id,
    text: item.label,
  })),
])

const alert = useAlert()
const logger = useLogger()

const removeConfirmationModal = useModal()

const includesUserNames = ref<string[]>([])
const includesUserNameModal = useModal()

const load = () => {
  if (!selectedItem.value) return
  const label = selectedItem.value.label
  const body = selectedItem.value.body
  const selections = selectedItem.value?.selections ?? []
  emit('load', body, selections)
  alert.success(`テンプレート「${label}」を本文末尾に挿入しました`)

  logger.ltlPeople.event.insertKarteCommentTemplate(logger.buildMeta(), {
    data: {
      template: selectedItem.value,
    },
  })
}

const editTemplate = () => {
  if (!selectedItem.value) return
  emit('edit')
  edit()

  logger.ltlPeople.event.editKarteCommentTemplate(logger.buildMeta(), {
    data: {
      template: selectedItem.value,
    },
  })
}

const cancelEditingTemplate = () => {
  if (!selectedItem.value) return
  const cancelledInput = editingItemState.value
  emit('finishEdit')
  cancelEditing()

  logger.ltlPeople.event.cancelEditingKarteCommentTemplate(logger.buildMeta(), {
    data: {
      template: selectedItem.value,
      cancelledInput,
    },
  })
}

const requestRemoveTemplate = () => {
  removeConfirmationModal.openModal()
}

const closeRemoveConfirmationTemplate = () => {
  removeConfirmationModal.closeModal()
}

const isDeleting = ref(false)

const removeTemplate = async () => {
  if (!selectedItem.value || isDeleting.value) return
  try {
    isDeleting.value = true

    // あとでログに出力したり、削除完了を表示するために削除される前にコピーしておく
    const targetItem = { ...selectedItem.value }

    await removeEditingItem()

    removeConfirmationModal.closeModal()
    alert.success(`テンプレート「${targetItem.label}」を削除しました`)

    logger.ltlPeople.event.removeKarteCommentTemplate(logger.buildMeta(), {
      data: {
        template: targetItem,
      },
    })

    emit('finishEdit')
  } finally {
    isDeleting.value = false
  }
}

const { mutate: previewKartePlannerCommentTemplate } =
  usePreviewKartePlannerCommentTemplateMutation({})

const handleSubmitIncludesUserNameModal = async () => {
  if (isEditing.value) {
    await applyEditedTemplate()
  } else {
    await createTemplate()
  }

  includesUserNameModal.closeModal()
}

const handleApplyEditedTemplate = async () => {
  if (editingItemState.value === undefined) {
    return
  }

  const previewRes = await previewKartePlannerCommentTemplate({
    input: {
      label: editingItemState.value.label,
      body: editingItemState.value.body,
      selections: editingItemState.value.selections,
    },
  })
  if (!previewRes?.data) {
    throw new Error('Failed to previewKartePlannerCommentTemplate')
  }

  includesUserNames.value =
    previewRes.data.previewKartePlannerCommentTemplate.maybeUserNames

  if (includesUserNames.value.length > 0) {
    includesUserNameModal.openModal()
    return
  }

  await applyEditedTemplate()
}

const applyEditedTemplate = async () => {
  const label = selectedItem.value?.label ?? ''
  emit('finishEdit')
  await applyEdited()
  // ${label}を更新しました、だと、更新する前のタイトルが表示されることになる
  alert.success(`テンプレート「${label}」を更新しました`)

  logger.ltlPeople.event.updateKarteCommentTemplate(logger.buildMeta(), {
    data: {
      template: editingItemState.value,
    },
  })
}

const handleCreateTemplate = async () => {
  const label = props.body.slice(0, DEFAULT_LABEL_LENGTH) + '…'

  const previewRes = await previewKartePlannerCommentTemplate({
    input: {
      label: label,
      body: props.body,
      selections: props.selections,
    },
  })
  if (!previewRes?.data) {
    throw new Error('Failed to previewKartePlannerCommentTemplate')
  }

  includesUserNames.value =
    previewRes.data.previewKartePlannerCommentTemplate.maybeUserNames

  if (includesUserNames.value.length > 0) {
    includesUserNameModal.openModal()
    return
  }

  await createTemplate()
}

const createTemplate = async () => {
  const label = props.body.slice(0, DEFAULT_LABEL_LENGTH) + '…'

  const newItem = await appendNewItem(label, props.body, props.selections)
  alert.success(`テンプレート「${label}」を新規追加しました`)

  logger.ltlPeople.event.createKarteCommentTemplate(logger.buildMeta(), {
    data: {
      template: newItem,
    },
  })
}

const selectTemplate = () => {
  if (selectedItem.value) {
    logger.ltlPeople.event.selectKarteCommentTemplate(logger.buildMeta(), {
      data: {
        template: selectedItem.value,
      },
    })
  }
}
</script>

<style lang="scss" module>
.container {
  $root: &;

  display: flex;
  align-items: center;
  font-size: 12px;

  &--editing {
    display: block;
  }

  &__selector {
    flex-shrink: 0;
  }

  &__actions {
    &:nth-child(n + 2) {
      margin-left: 24px;

      #{$root}--editing & {
        margin-top: 48px;
        margin-left: 0;
      }
    }
  }

  &__body {
    margin-top: 8px;
  }

  &__selections {
    margin-top: 8px;
  }
}

.selector {
  display: flex;
  align-items: center;

  &__action {
    &:nth-child(n + 2) {
      margin-left: 8px;
    }
  }
}

.buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  &__item {
    &:first-child {
      margin-right: auto;
    }

    &:nth-child(n + 2) {
      margin-left: 16px;
    }
  }
}
</style>
